// Leaves, bark and clippings: a few hundred particles with friction that
// pile against fences and obstacles and can be blown off the property.

import type { Layout, Leaves } from './types';
import type { Ctx } from './ctx';
import { cellAt, isSolidType, T_BED, T_OUT, T_PAVED, type SimGrid } from './layout';
import { randInt, type Rng } from './rng';
import { lockOnTarp } from './tools/tarp';

export const LEAF_LOOSE = 0;
export const LEAF_LOCKED = 1;
export const LEAF_BAGGED = 2;
export const LEAF_OFF = 3;
/** Velocity decay per second (exponential). */
export const LEAF_FRICTION = 3;
/**
 * Feature P6 (debris rules): velocity decay per second for an un-raked leaf in
 * a bed. Here rather than debris.ts: contract → spawn → leaves must not reach
 * props.ts (it imports contract), or module init breaks.
 */
export const BED_FRICTION = 12;
/** Below this speed (m/s) a leaf is at rest (and can lock on a tarp). */
export const LEAF_REST = 0.2;

export function spawnLeaves(layout: Layout, grid: SimGrid, rng: Rng): Leaves {
  const [lo, hi] = layout.leaves.count;
  let n = Math.max(0, randInt(rng, lo, hi));
  const free: number[] = [];
  for (let i = 0; i < grid.type.length; i++) if (!isSolidType(grid.type[i])) free.push(i);
  if (!free.length) n = 0;
  const L: Leaves = {
    count: n,
    x: new Float32Array(n), y: new Float32Array(n), vx: new Float32Array(n), vy: new Float32Array(n),
    kind: new Uint8Array(n), state: new Uint8Array(n),
  };
  const cm = grid.cellM;
  const areas = layout.leaves.areas ?? [];
  const total = areas.reduce((s, a) => s + a.w * a.h, 0);
  const big = layout.locationGroup === 'ovals' || (layout.gameScale ?? 1) > 1.5;
  for (let k = 0; k < n; k++) {
    let x = 0;
    let y = 0;
    let ok = false;
    if (total > 0) {
      for (let t = 0; t < 20 && !ok; t++) {
        let r = rng() * total;
        let a = areas[areas.length - 1];
        for (const cand of areas) {
          r -= cand.w * cand.h;
          if (r <= 0) {
            a = cand;
            break;
          }
        }
        x = a.x + rng() * a.w;
        y = a.y + rng() * a.h;
        const i = cellAt(grid, x, y);
        ok = i >= 0 && !isSolidType(grid.type[i]);
      }
    }
    if (!ok) {
      const i = free[Math.floor(rng() * free.length)];
      x = ((i % grid.w) + 0.1 + 0.8 * rng()) * cm;
      y = (Math.floor(i / grid.w) + 0.1 + 0.8 * rng()) * cm;
    }
    L.x[k] = x;
    L.y[k] = y;
    const r = rng();
    L.kind[k] = big ? (r < 0.5 ? 1 : r < 0.8 ? 2 : 0) : r < 0.8 ? 0 : 1;
  }
  return L;
}

export function stepLeaves(ctx: Ctx, dt: number): void {
  const L = ctx.leaves;
  const g = ctx.grid;
  const decay = Math.exp(-LEAF_FRICTION * dt);
  const bedDecay = Math.exp(-BED_FRICTION * dt);
  const tarp = ctx.tarp.placed;
  const debris = ctx.debris;
  for (let k = 0; k < L.count; k++) {
    if (L.state[k] !== LEAF_LOOSE) continue;
    let vx = L.vx[k];
    let vy = L.vy[k];
    if (vx !== 0 || vy !== 0) {
      const nx = L.x[k] + vx * dt;
      const ny = L.y[k] + vy * dt;
      const ci = cellAt(g, nx, ny);
      // Feature P6: only the rake takes a leaf out of a bed, and an un-raked
      // leaf entering (or pushed about in) a bed slows hard.
      let stuck = false;
      if (debris && !ctx.leafRaked[k]) {
        const toBed = ci >= 0 && g.type[ci] === T_BED;
        const fi = cellAt(g, L.x[k], L.y[k]);
        if (fi >= 0 && g.type[fi] === T_BED && !toBed) {
          L.vx[k] = 0;
          L.vy[k] = 0;
          if (tarp) lockOnTarp(ctx, k);
          continue;
        }
        stuck = toBed;
      }
      if (ci < 0 || g.type[ci] === T_OUT) {
        L.state[k] = LEAF_OFF;
        L.vx[k] = 0;
        L.vy[k] = 0;
        continue;
      }
      if (isSolidType(g.type[ci])) {
        vx = 0;
        vy = 0;
      } else {
        L.x[k] = nx;
        L.y[k] = ny;
        vx *= stuck ? bedDecay : decay;
        vy *= stuck ? bedDecay : decay;
        if (vx * vx + vy * vy < 0.0025) {
          vx = 0;
          vy = 0;
        }
      }
      L.vx[k] = vx;
      L.vy[k] = vy;
      if (debris && vx === 0 && vy === 0) ctx.leafRaked[k] = 0;
    }
    if (tarp && vx * vx + vy * vy < LEAF_REST * LEAF_REST) lockOnTarp(ctx, k);
  }
}

/**
 * Counts for the blow/collect task bars. `dirty` (Feature P6, debris rules
 * only, else 0): loose leaves on paved, bed or deck/step cells that no
 * catcher pass has reached.
 */
export function leafCounts(ctx: Ctx): { total: number; looseOnPaved: number; locked: number; bagged: number; off: number; dirty: number } {
  const L = ctx.leaves;
  const g = ctx.grid;
  const debris = ctx.debris;
  let looseOnPaved = 0;
  let locked = 0;
  let bagged = 0;
  let off = 0;
  let dirty = 0;
  for (let k = 0; k < L.count; k++) {
    const s = L.state[k];
    if (s === LEAF_LOOSE) {
      const i = cellAt(g, L.x[k], L.y[k]);
      if (i >= 0 && g.type[i] === T_PAVED) looseOnPaved++;
      if (debris && i >= 0 && ctx.debrisCell[i] && !ctx.leafCaught[k]) dirty++;
    } else if (s === LEAF_LOCKED) locked++;
    else if (s === LEAF_BAGGED) bagged++;
    else off++;
  }
  return { total: L.count, looseOnPaved, locked, bagged, off, dirty };
}
